import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Checkbox, Drawer, Input, Select, Space, Tag, Typography } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import {
  ACL_REASON_CODES,
  confine,
  createGrant,
  isLiveGrant,
  listGrants,
  type AclCapability,
  type AclReasonCode,
  type GrantView,
} from '@/api/acl';
import { listSpokes } from '@/api/tenantAdmin';
import { normalizeApiError } from '@/api/errors';
import type { Spoke } from '@/api/types';

const { Paragraph, Text } = Typography;

interface Props {
  /** The flow being fenced; null closes the drawer. */
  flowId: string | null;
  flowName?: string;
  onClose: () => void;
  /** Called after a successful assignment (the caller may refresh its list). */
  onAssigned?: (spokeId: string) => void;
}

/** The step-up wording every ACL write surface repeats (the PublishModal pattern). */
export const STEP_UP_MESSAGE =
  'Access fencing is a step-up lane: re-authenticate with a second factor (loa2) and retry.';

/**
 * OPSFL-FLOWOPS-1 C3 — "Assign to spoke": the console face of UC1 §4.7's SPOKE grantee. Composes
 * the two EXISTING ACL writes into one ring-fence gesture:
 *
 *  1. CONFINE (SPOKE s, flow) — s's people see and start NOTHING of type flow until granted
 *     (skipped when a live confinement already exists: one live CONFINE per grantee × type);
 *  2. ALLOW (SPOKE s, flow F, view[+work][+approve]) — F appears in s's catalogue (view) and,
 *     since C1 mapped runs.start → (flow, work), can be started by s (work).
 *
 * The SERVER is the gate: acl.confinements.write / acl.grants.write are hub_admin-only step-up
 * lanes; a 403 STEPUP_REQUIRED is surfaced verbatim, never swallowed. Nothing here changes the
 * hub-grained flow model — the flow stays the hub's; the spoke is granted a view of it.
 */
export function AssignSpokeDrawer({ flowId, flowName, onClose, onAssigned }: Props) {
  const { message } = App.useApp();
  const open = flowId != null;

  const [spokes, setSpokes] = useState<Spoke[] | null>(null);
  const [spokesUnavailable, setSpokesUnavailable] = useState(false);
  const [existing, setExisting] = useState<GrantView[]>([]);
  const [spokeId, setSpokeId] = useState<string>('');
  const [canWork, setCanWork] = useState(true);
  const [canApprove, setCanApprove] = useState(false);
  const [ringFence, setRingFence] = useState(true);
  const [reasonCode, setReasonCode] = useState<AclReasonCode>('SCOPE_CONFINEMENT');
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<{ code: string; message: string; stage: string } | null>(
    null,
  );

  const reset = useCallback(() => {
    setSpokeId('');
    setCanWork(true);
    setCanApprove(false);
    setRingFence(true);
    setReasonCode('SCOPE_CONFINEMENT');
    setFailure(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    let cancel = false;
    listSpokes()
      .then((l) => {
        if (!cancel) {
          setSpokes(l.spokes);
          setSpokesUnavailable(false);
        }
      })
      .catch(() => {
        // The spoke inventory is a convenience — an unreachable list degrades to a typed id.
        if (!cancel) {
          setSpokes([]);
          setSpokesUnavailable(true);
        }
      });
    listGrants({ resourceType: 'flow', resourceId: flowId ?? undefined })
      .then((g) => {
        if (!cancel) setExisting(g);
      })
      .catch(() => {
        if (!cancel) setExisting([]);
      });
    return () => {
      cancel = true;
    };
  }, [open, flowId, reset]);

  const assignedSpokes = useMemo(
    () =>
      existing
        .filter((g) => g.grantKind === 'ALLOW' && g.granteeType === 'SPOKE' && isLiveGrant(g))
        .map((g) => g.grantee),
    [existing],
  );
  const alreadyAssigned = spokeId !== '' && assignedSpokes.includes(spokeId);

  const submit = useCallback(async () => {
    if (!flowId || !spokeId.trim()) return;
    const spoke = spokeId.trim();
    const capabilities: AclCapability[] = ['view'];
    if (canWork) capabilities.push('work');
    if (canApprove) capabilities.push('approve');
    setSubmitting(true);
    setFailure(null);
    let stage = 'confinement';
    try {
      if (ringFence) {
        // One live CONFINE per (grantee, type): re-confining is refused by the store, so look
        // first — the confinement may already be in place from an earlier assignment.
        const live = (await listGrants({ granteeType: 'SPOKE', grantee: spoke, resourceType: 'flow' }))
          .some((g) => g.grantKind === 'CONFINE' && isLiveGrant(g));
        if (!live) {
          await confine({
            granteeType: 'SPOKE',
            grantee: spoke,
            resourceType: 'flow',
            reasonCode,
          });
        }
      }
      stage = 'grant';
      await createGrant({
        granteeType: 'SPOKE',
        grantee: spoke,
        resourceType: 'flow',
        resourceId: flowId,
        capabilities,
        reasonCode,
      });
      message.success(
        `'${flowId}' assigned to spoke '${spoke}' (${capabilities.join(', ')}) — bites on their next request.`,
      );
      onAssigned?.(spoke);
      onClose();
    } catch (e) {
      const err = normalizeApiError(e);
      if (err.httpStatus === 403 && err.code === 'STEPUP_REQUIRED') {
        message.error(STEP_UP_MESSAGE);
      } else {
        message.error(err.message);
      }
      setFailure({ code: err.code, message: err.message, stage });
    } finally {
      setSubmitting(false);
    }
  }, [flowId, spokeId, canWork, canApprove, ringFence, reasonCode, message, onAssigned, onClose]);

  const spokeOptions = useMemo(
    () =>
      (spokes ?? []).map((s) => ({
        value: s.spokeId,
        label: s.displayName ? `${s.displayName} (${s.spokeId})` : s.spokeId,
      })),
    [spokes],
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      title={
        <Space>
          <SafetyCertificateOutlined />
          <span>
            Assign <Text className="ol-mono">{flowId}</Text> to a spoke
          </span>
        </Space>
      }
      extra={
        <Button
          type="primary"
          onClick={() => void submit()}
          loading={submitting}
          disabled={!spokeId.trim() || alreadyAssigned}
        >
          Assign
        </Button>
      }
    >
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        <Paragraph style={{ margin: 0, fontSize: 13 }}>
          {flowName ? <b>{flowName}</b> : 'This flow'} stays the hub&apos;s. Assigning it grants the
          spoke&apos;s people a <b>view</b> of it (it appears in their Flows), optionally the right
          to <b>start runs</b> of it, and — with the ring-fence on — confines them to assigned
          flows only, so an unassigned hub flow can neither be seen nor started by id.
        </Paragraph>

        {assignedSpokes.length > 0 && (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Already assigned to:
            </Text>{' '}
            {assignedSpokes.map((s) => (
              <Tag key={s} style={{ marginInlineEnd: 4 }}>
                {s}
              </Tag>
            ))}
          </div>
        )}

        <div>
          <Text strong style={{ fontSize: 12 }}>
            Spoke
          </Text>
          {spokesUnavailable || (spokes && spokes.length === 0) ? (
            <Input
              placeholder="spoke id (e.g. branch-north)"
              value={spokeId}
              onChange={(e) => setSpokeId(e.target.value)}
              aria-label="Spoke id"
              style={{ marginTop: 4 }}
            />
          ) : (
            <Select
              showSearch
              placeholder="Choose a spoke"
              value={spokeId || undefined}
              onChange={(v) => setSpokeId(v)}
              options={spokeOptions}
              loading={spokes === null}
              style={{ width: '100%', marginTop: 4 }}
              aria-label="Spoke"
              optionFilterProp="label"
            />
          )}
          {alreadyAssigned && (
            <Text type="warning" style={{ fontSize: 12 }}>
              This spoke already holds a live assignment for this flow — revoke it under Tenant
              admin → Access fencing before re-granting with different capabilities.
            </Text>
          )}
        </div>

        <div>
          <Text strong style={{ fontSize: 12 }}>
            What the spoke may do
          </Text>
          <div style={{ display: 'grid', gap: 4, marginTop: 4 }}>
            <Checkbox checked disabled>
              See the flow (view) — always part of an assignment
            </Checkbox>
            <Checkbox checked={canWork} onChange={(e) => setCanWork(e.target.checked)}>
              Start runs of it (work) — needs a role holding <Text code>runs.start</Text>
            </Checkbox>
            <Checkbox checked={canApprove} onChange={(e) => setCanApprove(e.target.checked)}>
              Publish it (approve) — needs a role holding <Text code>flows.publish</Text>
            </Checkbox>
          </div>
        </div>

        <div>
          <Checkbox checked={ringFence} onChange={(e) => setRingFence(e.target.checked)}>
            Ring-fence the spoke to assigned flows only (confinement)
          </Checkbox>
          <Paragraph type="secondary" style={{ margin: '4px 0 0 24px', fontSize: 12 }}>
            Adds one confinement row for this spoke × flows (skipped if one is already live).
            Off = the spoke keeps seeing the whole hub catalogue and this grant only matters for
            restricted flows.
          </Paragraph>
        </div>

        <div>
          <Text strong style={{ fontSize: 12 }}>
            Reason
          </Text>
          <Select
            value={reasonCode}
            onChange={(v) => setReasonCode(v)}
            options={ACL_REASON_CODES.map((r) => ({ value: r, label: r }))}
            style={{ width: '100%', marginTop: 4 }}
            aria-label="Reason code"
          />
        </div>

        {failure && (
          <Alert
            type="error"
            showIcon
            style={{ borderRadius: 8 }}
            message={
              failure.code === 'STEPUP_REQUIRED'
                ? 'Step-up required'
                : `Refused at the ${failure.stage} step`
            }
            description={
              <span>
                <Text code>{failure.code}</Text> {failure.message}
                {failure.stage === 'grant' && ringFence
                  ? ' The confinement is in place; until the grant succeeds the spoke sees no flows (deny-closed). Retry, or revoke the confinement under Access fencing.'
                  : ''}
              </span>
            }
          />
        )}

        <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
          Server-enforced (hub admin, step-up lane). Revoke or review every fence under Tenant
          admin → Access fencing.
        </Paragraph>
      </Space>
    </Drawer>
  );
}
